import { useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import {
  ClickawayListener,
  CreditDisclosure,
  LinkToRedirect,
  StarRatingEditor,
  useAwardsBadgeContext,
} from '../../'
import { CardTile } from './card-tile'
import { endsInS, nextImageHelper } from '../../../utils'
import { CompareCredit } from '../../../../types/compare-credit'
import { ApplyNowIcon } from 'components/link-to-redirect/ApplyNowIcon'
import { clickRatesAndFees } from '../../../clients/segment'
import cn from 'utils/classnames'

export const HorizontelTable: React.FC<{
  card: CompareCredit.FormattedCard
  cardOrderIds: Record<string, string>
  categoryId: string
  externalId: string
  referencedCards: null | Record<string, CompareCredit.Entity>
}> = ({ card, cardOrderIds, categoryId, externalId, referencedCards }) => {
  const { getBadgeDataInAnyPosition } = useAwardsBadgeContext()

  const { showBadge, badgeTitle } = useMemo(
    () => getBadgeDataInAnyPosition(card.slug),
    [card.slug, getBadgeDataInAnyPosition],
  )

  const [openDetail, setOpenDetail] = useState<boolean>(false)

  const handleToggle = () => {
    setOpenDetail(!openDetail)
  }

  return (
    <tr key={card._id} className="relative / border-b border-slate-200">
      <td
        className={cn(
          'w-[10rem] lg:w-[7rem] xl:w-[9rem] / pr-3 / pb-3 lg:pb-4',
          { 'pt-12': showBadge, 'pt-4': !showBadge },
        )}
      >
        {showBadge && (
          <div className="c-card-table-hori__banner / absolute top-4 left-0 / flex justify-center / uppercase text-primary font-bold text-center tracking-wide leading-snug text-sm">
            <span className="inline-block / shrink-0 / w-3.5 / mr-2">
              <Image
                width={16}
                height={16}
                alt="CompareCredit Awards Ribbon"
                src="/static/awards/cc-awards-ribbon-small-clr.svg"
                aria-hidden={true}
                sizes="100vw"
                style={nextImageHelper.responsive}
              />
            </span>{' '}
            <span>{badgeTitle}</span>
          </div>
        )}
        <CardTile
          card={card}
          orderId={cardOrderIds[card.slug]}
          categoryId={categoryId}
          externalId={externalId}
          referencedCards={referencedCards}
        />
      </td>
      <td
        className={cn('w-[10rem] xl:w-[12.5rem] / pr-3 / pb-3 lg:pb-4', {
          'pt-12': showBadge,
          'pt-4': !showBadge,
        })}
      >
        {card.aprIntroBalanceTransfer &&
          card.aprIntroBalanceTransfer !== 'N/A' && (
            <div>
              <b className="text-primary">Balance Transfer</b>
              <div>
                {card.issuer.slug.current === 'bank-of-america'
                  ? `${card.aprIntroBalanceTransfer}.  A fee of ${card.feeBalanceTransfer} applies to all balance transfers`
                  : card.aprIntroBalanceTransfer}
              </div>
            </div>
          )}
        {card.aprIntroPurchase && card.aprIntroPurchase !== 'N/A' && (
          <div
            className={`${
              card.aprIntroBalanceTransfer &&
              card.aprIntroBalanceTransfer !== 'N/A' &&
              'mt-2'
            }`}
          >
            <b className="text-primary">Purchase APR</b>
            <div>{card.aprIntroPurchase}</div>
          </div>
        )}
        {((!card.aprIntroBalanceTransfer && !card.aprIntroPurchase) ||
          (card.aprIntroBalanceTransfer === 'N/A' &&
            card.aprIntroPurchase === 'N/A')) && <div>{'N/A'}</div>}
      </td>
      <td
        className={cn('w-[10rem] xl:w-[12.5rem] / pr-3 / pb-3 lg:pb-4', {
          'pt-12': showBadge,
          'pt-4': !showBadge,
        })}
      >
        {card.issuer.slug.current === 'bank-of-america'
          ? card.regularApr
          : card.aprBalanceTransfer}
      </td>
      <td
        className={cn('w-[7rem] xl:w-[8rem] / pr-3 / pb-3 lg:pb-4', {
          'pt-12': showBadge,
          'pt-4': !showBadge,
        })}
      >
        <div className="block / mb-1 / text-base font-semibold text-primary leading-tight">
          <span className="inline / mb-1 / text-base font-semibold text-primary leading-tight">
            {card.welcomeOfferValue ? card.welcomeOfferValue : 'N/A'}
          </span>

          <ClickawayListener setState={setOpenDetail} cssStyling="inline">
            <div className="c-popup / inline / relative / ml-1">
              {card.bonusSignUp && (
                <button
                  className="text-sm text-slate-600 underline / opacity-40 / hover:opacity-80 transition-all"
                  onClick={() => handleToggle()}
                >
                  <div className="w-4 h-4">
                    <Image
                      alt="more information"
                      role="presentation"
                      src={`/static/icons/icon-info-circle-black.svg`}
                      height={20}
                      width={20}
                      aria-hidden={true}
                      style={nextImageHelper.intrinsic}
                    />
                  </div>
                </button>
              )}
              <div
                className={`c-popup__container inline-block / absolute -left-[1.5rem] top-full / cursor-default / w-full min-w-[inherit] w-[16rem] / mt-0.5 / font-normal text-fs13 text-left text-gray-700 / z-40 ${
                  openDetail ? 'block' : 'hidden'
                } `}
              >
                <div className="c-popup__content c-popup__arrow c-popup__arrow--left / relative / flex justify-center / rounded-lg px-3 py-2 bg-white shadow-lg / z-20">
                  {card.bonusSignUp ? card.bonusSignUp : 'N/A'}
                </div>
              </div>
            </div>
          </ClickawayListener>
        </div>
      </td>
      <td
        className={cn('w-[5rem] xl:w-[5.5rem] / pr-3 / pb-3 lg:pb-4', {
          'pt-12': showBadge,
          'pt-4': !showBadge,
        })}
      >
        {card.feeAnnual}
      </td>
      <td
        className={cn('w-[10rem] / pb-3 lg:pb-4 pr-3', {
          'pt-12': showBadge,
          'pt-4': !showBadge,
        })}
      >
        <div className="block lg:scale-[.8] lg:-ml-4 lg:-mt-1">
          <StarRatingEditor
            productSlug={card.slug}
            stars={card.expertReviewRating}
          />
        </div>
      </td>
      <td
        className={cn(
          'w-[10rem] xl:w-[11rem] / pb-3 lg:pb-4 / hidden lg:table-cell',
          { 'pt-12': showBadge, 'pt-4': !showBadge },
        )}
      >
        <LinkToRedirect
          externalId={externalId}
          linkParams={card.applyNowLinkParameters}
          orderId={cardOrderIds[card.slug]}
          slug={card.slug}
        >
          <div className="c-btn c-btn--primary / min-w-[8rem] / mb-2 / py-2.5 / text-base">
            Apply Now
            <span className="w-4 h-4 / ml-2">
              <ApplyNowIcon
                width={16}
                height={16}
                aria-hidden={true}
                sizes="100vw"
                style={nextImageHelper.responsive}
              />
            </span>
          </div>
        </LinkToRedirect>
        <p className="mb-0.5 / text-fs12 text-slate-600 / text-center leading-tight / text-shadow">
          {card.issuerSecureSite
            ? card.issuerSecureSite
            : `on ${card.issuer.name}'${
                !endsInS(card.issuer.name) ? `s` : ''
              } secure site`}
        </p>
        {card.ratesAndFees && card.ratesAndFees && (
          <div className="w-full / text-center h-6">
            {card.ratesAndFees && card.ratesAndFees !== 'N/A' && (
              <Link
                className="text-primary-bright text-xs"
                href={card.ratesAndFees}
                onClick={() => {
                  clickRatesAndFees({
                    applyNowLink: card.applyNowLink,
                    component: 'Card Comparison Table Vertical',
                    name: card.name,
                    url: window.location.href,
                  })
                }}
                rel="noopener noreferrer"
                target="_blank"
              >
                Rates &amp; Fees
              </Link>
            )}
            {card.issuer.slug.current === 'american-express' && (
              <p className="text-fs10 text-gray-600 / text-center / mt-1 / w-full leading-4">
                Terms Apply
              </p>
            )}
          </div>
        )}

        <div
          className={cn('leading-[1]', {
            'mt-6': card.issuer.slug.current === 'american-express',
          })}
        >
          <span className="c-rec-credit__text / flex justify-center items-top / text-fs11 text-center font-semibold text-primary">
            Recommended Credit{' '}
            <div className="relative -top-1 z-30 / inline-block scale-75">
              <CreditDisclosure iconColor={'gray'} />
            </div>
          </span>
          <div className="-mt-1 / text-fs11 text-slate-600 text-center">
            {card.creditRange
              ? `${card.creditRange.min}-${card.creditRange.max}`
              : 'N/A'}
          </div>
        </div>
      </td>
    </tr>
  )
}
